import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, ArrowRight, Globe } from 'lucide-react';
import { cn } from '../utils/cn';
import routes from '../routes.jsx';

function buildGroupLinks(group) {
  return routes
    .filter(r => r.menu && r.group === group)
    .map(r => ({ name: r.name, path: r.path, desc: r.desc || '', badge: r.badge || null }));
}

const standaloneLinks = routes.filter(r => r.menu && r.group === 'standalone');
const istituzionaleLinks = routes.filter(r => r.menu && r.group === 'istituzionale' && r.path !== '/contatti');
const healthSubLinks = buildGroupLinks('health')
  .filter(l => l.path !== '/sanita')
  .sort((a, b) => a.name.localeCompare(b.name, 'it'));
const globalprojectsSubLinks = buildGroupLinks('globalprojects')
  .filter(l => l.path !== '/global-projects');

// Ordine menu: Chi Siamo → Investimenti → Health & Wellbeing → Global Projects
const GROUPS = {
  investimenti: {
    label: 'Investimenti',
    hubPath: '/investimenti',
    links: buildGroupLinks('investimenti').sort((a, b) => a.name.localeCompare(b.name, 'it')),
  },
  health: {
    label: 'Health & Wellbeing',
    hubPath: '/sanita',
    links: healthSubLinks,
  },
  globalprojects: {
    label: 'Global Projects',
    hubPath: '/global-projects',
    desc: 'Economia circolare nell\'healthcare, Northern Metropolis HK e Smart Cities.',
    links: globalprojectsSubLinks,
  },
};

const SCROLL_THRESHOLD = 80;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openMobileGroup, setOpenMobileGroup] = React.useState(null);
  const [activeMenu, setActiveMenu] = React.useState(null);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();
  const closeTimer = React.useRef(null);

  const isHome = location.pathname === '/';
  const isGhost = isHome && !scrolled && !activeMenu;

  React.useEffect(() => {
    if (!isHome) { setScrolled(false); return; }
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  React.useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  const open = (id) => { clearTimeout(closeTimer.current); setActiveMenu(id); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setActiveMenu(null), 150); };
  const cancelClose = () => clearTimeout(closeTimer.current);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (links, hubPath) =>
    links.some(l => location.pathname === l.path) || (hubPath && location.pathname === hubPath);

  const group = activeMenu ? GROUPS[activeMenu] : null;

  const linkCls = (active) => cn(
    'text-sm font-semibold h-full flex items-center border-b-2 transition-colors duration-300',
    active
      ? isGhost ? 'text-white border-white/80' : 'text-primary border-accent'
      : isGhost
        ? 'text-white border-transparent hover:text-white/80'
        : 'text-gray-600 border-transparent hover:text-primary'
  );

  return (
    <>
      {/* Overlay mega-menu */}
      <div
        className={cn(
          'fixed inset-0 bg-black/25 z-30 transition-opacity duration-200',
          activeMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        style={{ top: '80px' }}
        onMouseEnter={scheduleClose}
      />

      <nav
        className={cn(
          'fixed w-full z-40 transition-all duration-300',
          isGhost
            ? 'bg-transparent border-b border-transparent'
            : 'bg-white border-b border-gray-200 shadow-sm'
        )}
      >
        {/* Backdrop blur + gradient sottile solo in ghost mode per rendere la navbar leggibile */}
        {isGhost && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(10,15,25,0.55) 0%, rgba(10,15,25,0.0) 100%)',
              backdropFilter: 'blur(0px)',
            }}
          />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between h-20 items-center">

            {/* DESKTOP sinistra */}
            <div className="hidden md:flex items-center gap-8 h-full">
              <Link
                to="/"
                className={cn(
                  'text-2xl font-display font-bold italic mr-2 shrink-0 transition-colors duration-300',
                  isGhost ? 'text-white' : 'text-primary'
                )}
                style={isGhost ? { textShadow: '0 1px 8px rgba(10,15,25,0.5)' } : {}}
              >
                EUROPROJECT
                <span className={cn(
                  'font-display font-bold text-2xl ml-1 italic transition-colors duration-300',
                  isGhost ? 'text-white/90' : 'text-accent'
                )}>
                  CONSULTING
                </span>
              </Link>

              {/* Chi Siamo */}
              {istituzionaleLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={linkCls(isActive(link.path))}
                  style={isGhost ? { textShadow: '0 1px 6px rgba(10,15,25,0.5)' } : {}}
                >
                  {link.name}
                </Link>
              ))}

              {standaloneLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={linkCls(isActive(link.path))}
                  style={isGhost ? { textShadow: '0 1px 6px rgba(10,15,25,0.5)' } : {}}
                >
                  {link.name}
                </Link>
              ))}

              {/* Investimenti → Health & Wellbeing → Global Projects */}
              {Object.entries(GROUPS).map(([id, g]) => (
                <div
                  key={id}
                  onMouseEnter={() => open(id)}
                  onMouseLeave={scheduleClose}
                  className="h-full flex items-center"
                >
                  <Link
                    to={g.hubPath}
                    className={linkCls(isGroupActive(g.links, g.hubPath) || activeMenu === id)}
                    style={isGhost ? { textShadow: '0 1px 6px rgba(10,15,25,0.5)' } : {}}
                  >
                    {g.label}
                    <ChevronDown className={cn('w-4 h-4 ml-1 transition-transform duration-200', activeMenu === id && 'rotate-180')} />
                  </Link>
                </div>
              ))}
            </div>

            {/* DESKTOP destra */}
            <div className="hidden md:flex items-center gap-4">
              <div className={cn('w-px h-5 transition-colors duration-300', isGhost ? 'bg-white/30' : 'bg-gray-200')} />
              <Link
                to="/contatti"
                className={cn(
                  'px-5 py-2.5 text-sm font-bold flex items-center gap-1 group transition-colors duration-300',
                  isGhost
                    ? 'bg-white/15 text-white border border-white/40 hover:bg-white/25 backdrop-blur-sm'
                    : 'bg-accent text-white hover:bg-accent-dark'
                )}
                style={isGhost ? { textShadow: '0 1px 6px rgba(10,15,25,0.4)' } : {}}
              >
                Contattaci
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            {/* MOBILE logo */}
            <div className="md:hidden">
              <Link
                to="/"
                className={cn(
                  'text-lg sm:text-2xl font-display font-bold italic leading-tight transition-colors duration-300',
                  isGhost ? 'text-white' : 'text-primary'
                )}
                style={isGhost ? { textShadow: '0 1px 8px rgba(10,15,25,0.5)' } : {}}
              >
                EUROPROJECT
                <span className={cn(
                  'font-display font-bold text-lg sm:text-2xl ml-1 italic transition-colors duration-300',
                  isGhost ? 'text-white/90' : 'text-accent'
                )}>
                  CONSULTING
                </span>
              </Link>
            </div>

            {/* MOBILE hamburger */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={cn(
                  'p-3 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors duration-300',
                  isGhost ? 'text-white hover:text-white/80' : 'text-gray-600 hover:text-primary'
                )}
                aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* MEGA-MENU pannello */}
        <div
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className={cn(
            'absolute left-0 right-0 top-full bg-white border-t-2 border-accent shadow-2xl transition-all duration-200 origin-top z-50',
            activeMenu
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-2 pointer-events-none'
          )}
        >
          {group && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <div className="grid grid-cols-12 gap-0">
                <div className="col-span-3 pr-10 border-r border-gray-100 flex flex-col justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">{group.label}</p>
                    <p className="text-sm text-gray-500 leading-relaxed">{group.desc}</p>
                    {group.hubPath && (
                      <Link
                        to={group.hubPath}
                        className="inline-flex items-center gap-1 text-xs font-bold text-primary uppercase tracking-widest hover:text-accent transition-colors mt-5"
                      >
                        Panoramica area <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>

                <div className="col-span-9 pl-10">
                  <div className={cn(
                    'grid gap-x-6 gap-y-1',
                    activeMenu === 'health' ? 'grid-cols-2' : group.links.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-3'
                  )}>
                    {group.links.map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={cn(
                          'group flex flex-col px-4 py-4 rounded-sm transition-colors hover:bg-gray-50 relative',
                          isActive(link.path) && 'bg-gray-50',
                          link.badge && 'ring-1 ring-[#e8a020]/60 hover:ring-[#e8a020]'
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={cn(
                            'text-sm font-bold transition-colors flex items-center gap-2',
                            isActive(link.path) ? 'text-accent' : 'text-gray-800 group-hover:text-accent'
                          )}>
                            {link.name}
                            {link.badge && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#e8a020]/10 text-[#c47a00] border border-[#e8a020]/40">
                                <Globe className="w-2.5 h-2.5" />
                                {link.badge}
                              </span>
                            )}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed">{link.desc}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MOBILE menu accordion */}
        {mobileOpen && (
          <div
            id="mobile-nav"
            className={cn(
              'md:hidden border-b border-gray-200 overflow-y-auto',
              isGhost ? 'bg-[rgba(10,15,25,0.92)]' : 'bg-white'
            )}
            style={{ maxHeight: 'calc(100dvh - 80px)' }}
          >
            <div className="px-4 py-3 space-y-0">
              {istituzionaleLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'block py-4 text-sm font-medium border-b',
                    isGhost ? 'border-white/10 text-white/80' : 'border-gray-100 text-gray-700',
                    isActive(link.path) && (isGhost ? 'text-white' : 'text-primary')
                  )}
                >
                  {link.name}
                </Link>
              ))}

              {standaloneLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'block py-4 text-sm font-medium border-b',
                    isGhost ? 'border-white/10 text-white/80' : 'border-gray-100 text-gray-700',
                    isActive(link.path) && (isGhost ? 'text-white' : 'text-primary')
                  )}
                >
                  {link.name}
                </Link>
              ))}

              {/* Investimenti → Health & Wellbeing → Global Projects */}
              {Object.entries(GROUPS).map(([id, g]) => (
                <div key={id}>
                  <div className={cn('flex border-b', isGhost ? 'border-white/10' : 'border-gray-100')}>
                    <Link
                      to={g.hubPath}
                      onClick={() => setMobileOpen(false)}
                      className={cn('flex-1 py-4 text-sm font-medium text-left', isGhost ? 'text-white/80' : 'text-gray-700')}
                    >
                      {g.label}
                    </Link>
                    <button
                      onClick={() => setOpenMobileGroup(openMobileGroup === id ? null : id)}
                      className={cn(
                        'px-3 py-4 min-w-[44px] min-h-[44px] flex items-center justify-center',
                        isGhost ? 'text-white/50' : 'text-gray-500'
                      )}
                      aria-label="Espandi sottomenu"
                    >
                      <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', openMobileGroup === id && 'rotate-180')} />
                    </button>
                  </div>
                  {openMobileGroup === id && (
                    <div className={cn('py-1', isGhost ? 'bg-white/5' : 'bg-gray-50')}>
                      {g.links.map(link => (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            'flex items-center justify-between px-6 py-3 text-sm border-b',
                            isGhost ? 'border-white/10 text-white/70' : 'border-gray-100 text-gray-600',
                            isActive(link.path) && 'text-accent font-semibold'
                          )}
                        >
                          <span>{link.name}</span>
                          {link.badge && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#e8a020]/10 text-[#c47a00] border border-[#e8a020]/40">
                              <Globe className="w-2.5 h-2.5" />
                              {link.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <Link
                to="/contatti"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center bg-accent text-white py-4 text-sm font-bold mt-3"
              >
                Contattaci
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
